/**
 * SPRINT RECIBOS (2026-06) — Archivo digital de comprobantes de gastos.
 * Sección "Recibos" dentro de Contabilidad. Mobile-first, admin-only.
 * Solo: guardar → buscar → ver → descargar → imprimir → editar → eliminar.
 * Sin cálculos contables, sin OCR/IA, sin conexión bancaria.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Inventario / Ropa",
  "Materiales",
  "Publicidad",
  "Equipos / Maquinaria",
  "Oficina",
  "Vehículo",
  "Servicios",
  "Comidas de negocio",
  "Reparaciones / Mantenimiento",
  "Envíos",
  "Otros",
];
const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);
const fmtDate = (iso) => {
  const [y, m, d] = (iso || "").split("-");
  return y ? `${m}/${d}/${y}` : iso;
};

export default function ReceiptsPanel() {
  const { t, lang } = useI18n();
  const MONTHS = lang === "en" ? MONTHS_EN : MONTHS_ES;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ offices: [], years: [] });
  const [q, setQ] = useState("");
  const [fYear, setFYear] = useState("");
  const [fMonth, setFMonth] = useState("");
  const [fCategory, setFCategory] = useState("");
  const [fOffice, setFOffice] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | receipt object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (fYear) params.year = fYear;
      if (fMonth) params.month = fMonth;
      if (fCategory) params.category = fCategory;
      if (fOffice) params.office = fOffice;
      const { data } = await axios.get(`${API}/api/finance/receipts`, { params, withCredentials: true });
      setItems(data);
    } catch {
      toast.error(t("rc_load_err"));
    } finally {
      setLoading(false);
    }
  }, [q, fYear, fMonth, fCategory, fOffice, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/finance/receipts/meta`, { withCredentials: true });
        setMeta(data);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  const officeName = (key) => (meta.offices.find((o) => o.key === key) || {}).name || "";

  const fetchBlobUrl = async (r) => {
    const res = await axios.get(`${API}/api/finance/receipts/${r.id}/file`, {
      responseType: "blob",
      withCredentials: true,
    });
    return URL.createObjectURL(new Blob([res.data], { type: r.file?.content_type || "application/octet-stream" }));
  };

  const viewReceipt = async (r) => {
    // Anti popup-blocker: ventana síncrona en el clic (mismo patrón PDF de GP).
    const w = window.open("", "_blank");
    try {
      const url = await fetchBlobUrl(r);
      if (w) w.location = url;
      else window.open(url, "_blank");
    } catch {
      if (w) w.close();
      toast.error(t("rc_file_err"));
    }
  };

  const downloadReceipt = async (r) => {
    try {
      const url = await fetchBlobUrl(r);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.file?.original_filename || "recibo";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("rc_file_err"));
    }
  };

  const printReceipt = async (r) => {
    const isPdf = (r.file?.content_type || "").includes("pdf");
    try {
      const url = await fetchBlobUrl(r);
      if (isPdf) {
        // Vista estándar del navegador: iframe oculto → print(). Fallback: nueva pestaña.
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          } catch {
            window.open(url, "_blank");
          }
        };
      } else {
        const w = window.open("", "_blank");
        if (!w) {
          toast.error(t("rc_file_err"));
          return;
        }
        w.document.write(
          `<html><head><title>${r.vendor || "Recibo"}</title><style>body{margin:0;text-align:center}img{max-width:100%}</style></head>` +
          `<body><img src="${url}" onload="window.focus();window.print()" /></body></html>`
        );
        w.document.close();
      }
    } catch {
      toast.error(t("rc_file_err"));
    }
  };

  const deleteReceipt = async (r) => {
    if (!window.confirm(t("rc_confirm_delete"))) return;
    try {
      await axios.delete(`${API}/api/finance/receipts/${r.id}`, { withCredentials: true });
      toast.success(t("rc_deleted"));
      load();
    } catch {
      toast.error(t("rc_delete_err"));
    }
  };

  // Agrupación AÑO → MES por fecha del recibo (no fecha de carga).
  const groups = [];
  for (const r of items) {
    const y = (r.receipt_date || "").slice(0, 4);
    const m = (r.receipt_date || "").slice(5, 7);
    let yg = groups.find((g) => g.year === y);
    if (!yg) {
      yg = { year: y, months: [] };
      groups.push(yg);
    }
    let mg = yg.months.find((x) => x.month === m);
    if (!mg) {
      mg = { month: m, items: [] };
      yg.months.push(mg);
    }
    mg.items.push(r);
  }

  return (
    <div data-testid="receipts-panel" className="space-y-4">
      <button
        data-testid="rc-add-btn"
        onClick={() => setEditing("new")}
        className="w-full sm:w-auto px-6 py-4 sm:py-3 font-mono-tech text-sm font-bold bg-black text-white border border-black hover:bg-[var(--accent-magenta,#E5007D)] transition-colors"
      >
        + {t("rc_add")}
      </button>

      {editing && (
        <ReceiptForm
          receipt={editing === "new" ? null : editing}
          offices={meta.offices}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          t={t}
        />
      )}

      {/* Buscador + filtros */}
      <div className="bg-white border border-[var(--border)] p-3 space-y-3">
        <div className="flex gap-2">
          <input
            data-testid="rc-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("rc_search")}
            className="flex-1 border border-[var(--border)] px-3 py-3 text-base sm:text-sm min-w-0"
          />
          <button
            data-testid="rc-filters-toggle"
            onClick={() => setShowFilters((v) => !v)}
            className={`px-4 py-3 font-mono-tech text-xs font-bold border transition-colors whitespace-nowrap ${
              showFilters || fYear || fMonth || fCategory || fOffice
                ? "bg-black text-white border-black"
                : "border-[var(--border)] hover:bg-black hover:text-white"
            }`}
          >
            {t("rc_filters")}
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <select data-testid="rc-filter-year" value={fYear} onChange={(e) => setFYear(e.target.value)}
              className="border border-[var(--border)] px-2 py-3 text-sm bg-white">
              <option value="">{t("rc_year")}: {t("rc_all")}</option>
              {meta.years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select data-testid="rc-filter-month" value={fMonth} onChange={(e) => setFMonth(e.target.value)}
              className="border border-[var(--border)] px-2 py-3 text-sm bg-white">
              <option value="">{t("rc_month")}: {t("rc_all")}</option>
              {MONTHS.map((m, i) => (
                <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>
              ))}
            </select>
            <select data-testid="rc-filter-category" value={fCategory} onChange={(e) => setFCategory(e.target.value)}
              className="border border-[var(--border)] px-2 py-3 text-sm bg-white">
              <option value="">{t("rc_category")}: {t("rc_all")}</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <select data-testid="rc-filter-office" value={fOffice} onChange={(e) => setFOffice(e.target.value)}
              className="border border-[var(--border)] px-2 py-3 text-sm bg-white">
              <option value="">{t("rc_office")}: {t("rc_all")}</option>
              {meta.offices.map((o) => (
                <option key={o.key} value={o.key}>{o.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Listado AÑO → MES */}
      {loading && <div data-testid="rc-loading" className="text-sm font-mono-tech py-6">{t("fin_loading")}</div>}
      {!loading && items.length === 0 && (
        <div data-testid="rc-empty" className="text-sm italic text-[var(--text-secondary)] py-6">{t("rc_empty")}</div>
      )}
      {!loading &&
        groups.map((yg) => (
          <div key={yg.year} data-testid={`rc-year-${yg.year}`} className="space-y-3">
            <div className="font-display text-2xl font-black tracking-tight">{yg.year}</div>
            {yg.months.map((mg) => (
              <div key={mg.month} data-testid={`rc-month-${yg.year}-${mg.month}`} className="space-y-2">
                <div className="label-overline">{MONTHS[Number(mg.month) - 1] || mg.month} · {mg.items.length}</div>
                <div className="space-y-2">
                  {mg.items.map((r) => (
                    <div
                      key={r.id}
                      data-testid={`rc-card-${r.id}`}
                      className="bg-white border border-[var(--border)] p-3 sm:p-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-base font-bold truncate" data-testid={`rc-vendor-${r.id}`}>{r.vendor}</div>
                          <div className="text-xs text-[var(--text-secondary)] font-mono-tech mt-0.5">
                            {fmtDate(r.receipt_date)} · {r.category}
                            {r.office_key ? ` · ${officeName(r.office_key)}` : ""}
                          </div>
                        </div>
                        <div className="font-mono-tech text-lg font-black" data-testid={`rc-amount-${r.id}`}>{money(r.amount)}</div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <RcBtn testid={`rc-view-${r.id}`} onClick={() => viewReceipt(r)}>{t("rc_view")}</RcBtn>
                        <RcBtn testid={`rc-download-${r.id}`} onClick={() => downloadReceipt(r)}>{t("rc_download")}</RcBtn>
                        <RcBtn testid={`rc-print-${r.id}`} onClick={() => printReceipt(r)}>{t("rc_print")}</RcBtn>
                        <RcBtn testid={`rc-edit-${r.id}`} onClick={() => setEditing(r)}>{t("rc_edit")}</RcBtn>
                        <RcBtn testid={`rc-delete-${r.id}`} onClick={() => deleteReceipt(r)} danger>{t("rc_delete")}</RcBtn>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}

function RcBtn({ children, onClick, testid, danger }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`font-mono-tech text-xs font-bold px-3 py-2 border transition-colors ${
        danger
          ? "border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
          : "border-black hover:bg-black hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function ReceiptForm({ receipt, offices, onClose, onSaved, t }) {
  const isEdit = !!receipt;
  const [form, setForm] = useState({
    receipt_date: receipt?.receipt_date || new Date().toISOString().slice(0, 10),
    vendor: receipt?.vendor || "",
    amount: receipt?.amount != null ? String(receipt.amount) : "",
    category: receipt?.category || "",
    office_key: receipt?.office_key || "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const amt = Number(form.amount);
    if (!form.receipt_date || !form.vendor.trim() || form.amount === "" || Number.isNaN(amt) || amt < 0 || !form.category) {
      toast.error(t("rc_required"));
      return;
    }
    if (!isEdit && !file) {
      toast.error(t("rc_required"));
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("receipt_date", form.receipt_date);
      fd.append("vendor", form.vendor.trim());
      fd.append("amount", amt.toFixed(2));
      fd.append("category", form.category);
      fd.append("office_key", form.office_key);
      if (file) fd.append("file", file);
      if (isEdit) {
        await axios.patch(`${API}/api/finance/receipts/${receipt.id}`, fd, { withCredentials: true });
        toast.success(t("rc_updated"));
      } else {
        await axios.post(`${API}/api/finance/receipts`, fd, { withCredentials: true });
        toast.success(t("rc_saved"));
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("rc_err"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="rc-form" className="bg-white border-2 border-black p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="label-overline">{isEdit ? t("rc_edit_title") : t("rc_new_title")}</div>
        <button data-testid="rc-form-close" onClick={onClose} className="font-mono-tech text-xs font-bold border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white">
          ✕
        </button>
      </div>
      <div className="space-y-3">
        <RcField label={`${t("rc_date")} *`}>
          <input data-testid="rc-input-date" type="date" value={form.receipt_date} onChange={set("receipt_date")}
            className="w-full border border-[var(--border)] px-3 py-3 text-base sm:text-sm" />
        </RcField>
        <RcField label={`${t("rc_vendor")} *`}>
          <input data-testid="rc-input-vendor" value={form.vendor} onChange={set("vendor")} placeholder="Home Depot"
            className="w-full border border-[var(--border)] px-3 py-3 text-base sm:text-sm" />
        </RcField>
        <RcField label={`${t("rc_amount")} *`}>
          <input data-testid="rc-input-amount" type="number" inputMode="decimal" min="0" step="0.01" value={form.amount}
            onChange={set("amount")} placeholder="186.42"
            className="w-full border border-[var(--border)] px-3 py-3 text-base sm:text-sm font-mono-tech" />
        </RcField>
        <RcField label={`${t("rc_category")} *`}>
          <select data-testid="rc-input-category" value={form.category} onChange={set("category")}
            className="w-full border border-[var(--border)] px-3 py-3 text-base sm:text-sm bg-white">
            <option value="">—</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </RcField>
        <RcField label={t("rc_office")}>
          <select data-testid="rc-input-office" value={form.office_key} onChange={set("office_key")}
            className="w-full border border-[var(--border)] px-3 py-3 text-base sm:text-sm bg-white">
            <option value="">{t("rc_office_none")}</option>
            {offices.map((o) => (
              <option key={o.key} value={o.key}>{o.name}</option>
            ))}
          </select>
        </RcField>
        <RcField label={isEdit ? t("rc_file_replace") : `${t("rc_file")} *`}>
          <input
            ref={fileRef}
            data-testid="rc-input-file"
            type="file"
            accept="image/jpeg,image/jpg,image/png,application/pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full border border-[var(--border)] px-3 py-3 text-sm"
          />
          {file && (
            <div data-testid="rc-file-selected" className="text-xs font-mono-tech text-[var(--text-secondary)] mt-1">
              ✓ {file.name}
            </div>
          )}
          {isEdit && !file && (
            <div className="text-xs font-mono-tech text-[var(--text-secondary)] mt-1">
              {t("rc_current_file")}: {receipt.file?.original_filename}
            </div>
          )}
        </RcField>
      </div>
      <button
        data-testid="rc-save-btn"
        onClick={save}
        disabled={saving}
        className="w-full py-4 font-mono-tech text-sm font-bold bg-black text-white border border-black hover:bg-[var(--accent-magenta,#E5007D)] transition-colors disabled:opacity-40"
      >
        {t("rc_save")}
      </button>
    </div>
  );
}

function RcField({ label, children }) {
  return (
    <div>
      <label className="block font-mono-tech text-[0.65rem] font-bold tracking-wider text-[var(--text-secondary)] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
